import { isIsolatedCloud } from '@atlaskit/atlassian-context';
import {
	isPostOfficePlacementsGloballyDisabled,
	usePostOfficeContext,
	usePostOfficeRoute,
	useStableObject,
} from '@atlassian/post-office-context';
import {
	PerformanceTracking,
	PerformanceTrackingSegment,
	StableProviders,
} from '@post-office/placement-contexts/create-placement-component/providers';
import { withErrorBoundary } from '@post-office/placement-contexts/withErrorBoundary';
import { memo } from 'react';

import {
	type ErrorBoundariedPropsV2,
	type PlacementComponentPropsInternalV2,
	type PlacementComponentV2,
} from './types';
import type { PlacementConfigurationOptions } from '../types';

export const ERROR_BOUNDARY_LOCATION = 'create-placement-component';

export const createBasePlacementComponentV2 = <P,>(
	placementId: string,
	Component: PlacementComponentV2<P>,
	options: PlacementConfigurationOptions | undefined,
) =>
	withErrorBoundary<PlacementComponentPropsInternalV2<P>>(
		memo((props) => {
			// Hide placement in Isolated Cloud environments if configured
			// The typeof document check ensures we don't call isIsolatedCloud() during SSR
			if (options?.hideInIsolatedCloud && typeof document !== 'undefined' && isIsolatedCloud()) {
				return null;
			}

			const providedContext = usePostOfficeContext();

			const { onEvent, initialData, ...componentProps } = props;

			const context = props?.context ?? providedContext;

			if (isPostOfficePlacementsGloballyDisabled(context)) {
				return null;
			}

			const route = usePostOfficeRoute();

			const stableContext = useStableObject(context);

			return (
				<PerformanceTrackingSegment placementId={placementId}>
					<PerformanceTracking initialData={initialData} onEvent={onEvent}>
						<StableProviders
							route={route}
							context={stableContext}
							placement={{ placementId }}
							choreographer={options?.choreographer}
							subscription={options?.subscription}
							subscriptionAdapter={options?.subscriptionAdapter}
						>
							{/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
							<Component initialData={initialData} {...(componentProps as any)} />
						</StableProviders>
					</PerformanceTracking>
				</PerformanceTrackingSegment>
			);
		}),
	);

export const createPlacementComponentV2 = <P,>(
	placementId: string,
	Component: PlacementComponentV2<P>,
	options: PlacementConfigurationOptions | undefined,
) => {
	const placement = createBasePlacementComponentV2(placementId, Component, options);

	return memo((props: ErrorBoundariedPropsV2<P>) => {
		return placement({
			...props,
			fallbackRender: () => null,
		});
	});
};
